import { useEffect, useRef, type RefObject } from "react";

/**
 * Calls `onOutside` on any click outside every element in `refs`, only while `active`. Listens
 * on `mousedown` rather than `click` so it fires before whatever the click would otherwise
 * trigger underneath (e.g. selecting a map element).
 *
 * Accepts more than one ref because a portaled dropdown's content lives outside its trigger's
 * DOM subtree — a click inside the portaled content must still count as "inside", or the
 * dropdown would close itself the instant anything in it is clicked.
 */
export function useClickOutside(
  refs: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  onOutside: () => void,
  active: boolean
) {
  // `refs`/`onOutside` are routinely a fresh array literal/inline closure
  // at the call site every render — depending on them directly used to
  // re-subscribe the document listener on EVERY render, not just when
  // `active` flips. Read fresh from a ref at event-fire-time instead, kept
  // current by a plain per-render assignment (cheap, no effect of its own
  // needed) — the listener itself only ever gets added/removed when
  // `active` actually changes.
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;

  useEffect(() => {
    if (!active) return;
    function handlePointerDown(e: MouseEvent) {
      if (!(e.target instanceof Node)) return;
      const list = Array.isArray(refsRef.current) ? refsRef.current : [refsRef.current];
      const inside = list.some((ref) => ref.current?.contains(e.target as Node));
      if (!inside) onOutsideRef.current();
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [active]);
}
